import { useState, useEffect, useCallback } from 'react';
import { supabase, callEdgeFunction } from '../lib/supabase';

export function useApprentices(profile, orgId) {
  const [allProfiles, setAllProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadProfiles = useCallback(async () => {
    if (!orgId) return;
    const { data } = await supabase.from('profiles').select('*').eq('org_id', orgId);
    setAllProfiles(data || []);
    setLoading(false);
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    loadProfiles();
  }, [orgId, loadProfiles]);

  // Archived people stay in allProfiles so their name still resolves on old
  // evidence and reports — they're only filtered out of the active lists.
  const isActive = p => !p.archived_at;

  function getApprentices() {
    return allProfiles.filter(p => p.role === 'apprentice' && isActive(p));
  }

  function getArchivedApprentices() {
    return allProfiles.filter(p => p.role === 'apprentice' && p.archived_at);
  }

  function getTrainers() {
    return allProfiles.filter(p => p.role === 'trainer');
  }

  function getSupervisors() {
    return allProfiles.filter(p => p.role === 'supervisor');
  }

  function getAssignedApprentices(userId) {
    const user = allProfiles.find(p => p.id === userId);
    if (!user || !user.assigned_to) return [];
    return allProfiles.filter(p => (user.assigned_to || []).includes(p.id) && isActive(p));
  }

  function getMyApprentices() {
    if (!profile) return [];
    if (profile.role === 'employer') return getApprentices();
    if (profile.role === 'trainer') return getAssignedApprentices(profile.id);
    if (profile.role === 'supervisor') return getAssignedApprentices(profile.id);
    return [profile];
  }

  async function inviteUser(email, name, role, year, studentNumber) {
    const result = await callEdgeFunction('invite-user', {
      email, name, role, year, studentNumber,
      redirectTo: window.location.origin,
    });
    await loadProfiles();
    // emailSent tells the caller whether Supabase actually sent the invite, and
    // link is the fallback to pass on by hand when it didn't.
    return result;
  }

  async function assignTrainer(trainerId, apprenticeIds) {
    const { error } = await supabase
      .from('profiles')
      .update({ assigned_to: apprenticeIds })
      .eq('id', trainerId);
    if (error) throw error;
    await loadProfiles();
  }

  async function updateUser(userId, updates) {
    const { error } = await supabase.from('profiles').update(updates).eq('id', userId);
    if (error) throw error;
    await loadProfiles();
  }

  // The whole cascade — records, caseload assignments, profile and login — runs
  // inside the delete-user function. Doing it here first meant the profile row was
  // already gone by the time the function checked it, so it 404'd and the login
  // survived while every record was destroyed. One call, or nothing happens.
  async function deleteApprentice(apprenticeId) {
    await callEdgeFunction('delete-user', { apprenticeId });
    await loadProfiles();
  }

  // A sign-in link the employer can pass on by hand when the invite email never
  // turns up. Invalidates any link already emailed to that person.
  async function getSignInLink(email) {
    const result = await callEdgeFunction('invite-user', {
      action: 'link', email, redirectTo: window.location.origin,
    });
    return result.link;
  }

  // Archive keeps every record and revokes the login; restore puts them back.
  async function setArchived(userId, archive) {
    const result = await callEdgeFunction('archive-user', { userId, archive });
    await loadProfiles();
    return result;
  }

  return {
    allProfiles,
    loading,
    getApprentices,
    getArchivedApprentices,
    getTrainers,
    getSupervisors,
    getAssignedApprentices,
    getMyApprentices,
    inviteUser,
    getSignInLink,
    assignTrainer,
    updateUser,
    deleteApprentice,
    setArchived,
    reload: loadProfiles,
  };
}
